import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FuseConfigService } from '@fuse/services/config';
import { environment } from 'environments/environment';
import { cloneDeep, merge } from 'lodash';
import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { AuthConfig } from '../auth/models/auth-config.model';
import { IcosaedroConfig, IcosaedroConfigInput } from '../models/icosaedro-config.model';
import { appConfig } from './app.config';

@Injectable({
    providedIn: 'root',
})
export class ConfigurationService {
    private readonly MAX_SESSION_TIMEOUT_SECS = 120;
    private readonly DEFAULT_KEYCLOAK_UPDATE_MIN_VALIDITY_SECS = 60;
    private readonly DEFAULT_KEYCLOAK_TOKEN_REFRESH_INTERVAL_SECS = 240;
    private readonly DEFAULT_KEYCLOAK_CHECK_LOGIN_IFRAME = false;
    private config!: IcosaedroConfig;

    constructor(
        private httpClient: HttpClient,
        private fuseConfigService: FuseConfigService,
        private logger: NGXLogger
    ) {}

    public load(): Promise<IcosaedroConfig> {
        const jsonFile = environment.configFile;

        return new Promise((resolve, reject) => {
            firstValueFrom(this.httpClient.get<IcosaedroConfigInput>(jsonFile))
                .then((configuration: IcosaedroConfigInput) => {
                    this.config = this.adaptConfiguration(configuration);

                    this.initLogger(this.getLogLevelByKey(this.config.logLevel ?? ''));

                    this.configureFuse();

                    resolve(this.config);
                })
                .catch((error: unknown) => {
                    console.error('Cannot load configuration', error);
                    reject(error);
                });
        });
    }

    public getConfig(): IcosaedroConfig {
        return cloneDeep(this.config);
    }

    private adaptConfiguration(configuration: IcosaedroConfigInput): IcosaedroConfig {
        const features = new Map<string, boolean>();

        if (configuration.features) {
            for (const key in configuration.features) {
                features.set(key, configuration.features[key]);
            }
        }

        return {
            rootUrl: configuration.rootUrl!,
            biAlertUrl: configuration.biAlertUrl!,
            logLevel: configuration.logLevel ?? 'INFO',
            langDefault: configuration.langDefault ?? 'en',
            fuse: configuration.fuse!,
            auth: this.buildAuthConfig(configuration),
            features,
            allowedFileTypesToUpload: configuration.allowedFileTypesToUpload!,
            allowedSizeImageToUploadBytes: configuration.allowedSizeImageToUploadBytes!,
            allowedSizeVideoToUploadBytes: configuration.allowedSizeVideoToUploadBytes!,
            showSettings: configuration.showSettings ?? false,
            grafana: configuration.grafana!,
            domain: configuration.domain!,
            satelliteEmbed: configuration.satelliteEmbed,
        };
    }

    private buildAuthConfig(configuration: IcosaedroConfigInput): AuthConfig {
        const fromBlock = configuration.auth;

        let sessionIdleSeconds =
            fromBlock?.sessionIdleSeconds ??
            configuration.maxSessionTimeoutSeconds ??
            this.MAX_SESSION_TIMEOUT_SECS;

        if (sessionIdleSeconds < 0) {
            sessionIdleSeconds = this.MAX_SESSION_TIMEOUT_SECS;
        }

        let tokenMinValiditySeconds =
            fromBlock?.tokenMinValiditySeconds ??
            configuration.keycloakUpdateMinValiditySeconds ??
            this.DEFAULT_KEYCLOAK_UPDATE_MIN_VALIDITY_SECS;

        if (tokenMinValiditySeconds <= 0) {
            tokenMinValiditySeconds = this.DEFAULT_KEYCLOAK_UPDATE_MIN_VALIDITY_SECS;
        }

        let tokenRefreshIntervalSeconds =
            fromBlock?.tokenRefreshIntervalSeconds ??
            configuration.keycloakTokenRefreshIntervalSeconds ??
            this.DEFAULT_KEYCLOAK_TOKEN_REFRESH_INTERVAL_SECS;

        if (tokenRefreshIntervalSeconds <= 0) {
            tokenRefreshIntervalSeconds = this.DEFAULT_KEYCLOAK_TOKEN_REFRESH_INTERVAL_SECS;
        }

        const checkLoginIframe =
            fromBlock?.checkLoginIframe ??
            configuration.keycloakCheckLoginIframe ??
            this.DEFAULT_KEYCLOAK_CHECK_LOGIN_IFRAME;

        const redirectDebugEnabled =
            fromBlock?.redirectDebugEnabled ?? configuration.authRedirectDebugEnabled ?? false;

        const keycloak = fromBlock?.keycloak ?? configuration.keycloak;

        if (!keycloak?.url || !keycloak?.realm || !keycloak?.clientId) {
            throw new Error('Keycloak configuration is missing url, realm or clientId.');
        }

        return {
            sessionIdleSeconds,
            tokenMinValiditySeconds,
            tokenRefreshIntervalSeconds,
            checkLoginIframe,
            redirectDebugEnabled,
            keycloak,
        };
    }

    private getLogLevelByKey(value: string): NgxLoggerLevel {
        let level: NgxLoggerLevel;

        switch (value.toUpperCase()) {
            case 'TRACE':
                level = NgxLoggerLevel.TRACE;
                break;
            case 'DEBUG':
                level = NgxLoggerLevel.DEBUG;
                break;
            case 'INFO':
                level = NgxLoggerLevel.INFO;
                break;
            case 'LOG':
                level = NgxLoggerLevel.LOG;
                break;
            case 'WARN':
                level = NgxLoggerLevel.WARN;
                break;
            case 'ERROR':
                level = NgxLoggerLevel.ERROR;
                break;
            case 'FATAL':
                level = NgxLoggerLevel.FATAL;
                break;
            case 'OFF':
                level = NgxLoggerLevel.OFF;
                break;

            default:
                level = NgxLoggerLevel.INFO;
        }

        return level;
    }

    private initLogger(level: NgxLoggerLevel): void {
        const config = this.logger.getConfigSnapshot();
        config.level = level;

        this.logger.updateConfig(config);

        this.logger.log(`Logger configured with level ${level} ${NgxLoggerLevel[level]}`);
    }

    private configureFuse(): void {
        if (this.config.fuse) {
            this.fuseConfigService.config = merge({}, appConfig, this.config.fuse);
        } else {
            this.fuseConfigService.config = appConfig;
        }
    }
}
